import { useEffect, useMemo, useRef, useState } from "react";
import { api, imgUrl } from "../lib/api";
import { X, ChevronLeft, ChevronRight, ArrowDown } from "lucide-react";
import Seo from "../components/Seo";

// A single photo card — reveals on scroll and offers a subtle parallax lift.
function PhotoCard({ photo, size = "square", onClick, index }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.15, rootMargin: "0px 0px -80px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const aspects = {
    portrait: "aspect-[3/4]",
    landscape: "aspect-[16/10]",
    wide: "aspect-[21/9]",
    square: "aspect-square",
    tall: "aspect-[2/3]",
  };

  return (
    <button
      ref={ref}
      onClick={onClick}
      className={`group relative block w-full overflow-hidden rounded-2xl sm:rounded-3xl bg-[#151515] border border-white/10 float-card ${aspects[size] || aspects.square}`}
      style={{
        transitionDelay: `${index * 40}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? "translate3d(0,0,0)" : "translate3d(0,60px,0)",
        transition: "opacity .9s cubic-bezier(.2,.9,.25,1), transform .9s cubic-bezier(.2,.9,.25,1), box-shadow .5s ease",
        willChange: "transform, opacity",
      }}
      data-testid={`gallery-photo-${photo.id}`}
    >
      <img
        src={imgUrl(photo.image_path)}
        alt={photo.caption || "SyndaFam gallery photo"}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
      />
      {/* Subtle gradient scrim so caption is readable */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      {/* Caption reveal */}
      {photo.caption && (
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500 text-left">
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311] mb-1">// FRAME</div>
          <div className="font-display text-sm sm:text-base text-white leading-tight">{photo.caption}</div>
        </div>
      )}
      {/* Corner index chip */}
      <div className="absolute top-3 left-3 sm:top-4 sm:left-4 font-mono text-[9px] tracking-[0.3em] text-white/70 bg-black/40 backdrop-blur px-2 py-1 rounded-full border border-white/10">
        {String(index + 1).padStart(2, "0")}
      </div>
    </button>
  );
}

// A cinematic full-viewport section intro that leads into the grid.
function SectionIntro({ section, count, index }) {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0); // 0..1, used for parallax
  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const vh = window.innerHeight;
      // -1 (top far above viewport) to +1 (fully below)
      const p = 1 - Math.max(0, Math.min(1, (rect.top + rect.height * 0.5) / vh));
      setProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const shift = (progress - 0.5) * 60; // -30..+30

  return (
    <div ref={ref} className="relative min-h-[60vh] flex flex-col items-start justify-center py-24 sm:py-32">
      {/* Chapter number — light on transform, huge on scale */}
      <div
        className="font-display leading-[0.85] text-white/[0.05] absolute -left-2 sm:-left-6 top-4 pointer-events-none select-none"
        style={{
          fontSize: "clamp(11rem, 34vw, 32rem)",
          transform: `translate3d(0, ${shift * 0.6}px, 0)`,
          willChange: "transform",
        }}
        aria-hidden="true"
      >
        {String(index + 1).padStart(2, "0")}
      </div>

      <div
        className="relative reveal reveal-1"
        style={{ transform: `translate3d(0, ${shift * -0.3}px, 0)`, willChange: "transform" }}
      >
        <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311] mb-4">
          // CHAPTER {String(index + 1).padStart(2, "0")} · {count} FRAME{count === 1 ? "" : "S"}
        </div>
        <h2
          className="font-display leading-[0.9] tracking-tighter"
          style={{ fontSize: "clamp(3rem, 8vw, 8rem)" }}
          data-testid={`gallery-section-name-${section.position}`}
        >
          {section.name}
        </h2>
        {section.description && (
          <p className="mt-5 max-w-xl text-white/60 text-base sm:text-lg leading-relaxed">
            {section.description}
          </p>
        )}
      </div>
    </div>
  );
}

// Turn a flat list of photos into a curated dynamic layout by picking varied aspects.
function pickSizes(count) {
  // A hand-tuned rhythm that avoids monotony. Repeats after 6 cards.
  const pattern = ["landscape", "portrait", "square", "portrait", "landscape", "tall"];
  return Array.from({ length: count }).map((_, i) => pattern[i % pattern.length]);
}

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(-1); // -1 = closed

  useEffect(() => {
    Promise.all([
      api.get("/gallery").then((r) => r.data || []),
      api.get("/gallery/sections").then((r) => r.data || []),
    ])
      .then(([p, s]) => { setPhotos(p); setSections(s); })
      .finally(() => setLoading(false));
  }, []);

  // Flat photo list ordered by section then by original position, plus back-mapping
  const orderedPhotos = useMemo(() => {
    const list = [];
    const byPos = new Map(sections.map((s) => [s.position, s]));
    // Sections we know about, in section order
    for (const s of sections) {
      const bucket = photos.filter((p) => (p.section || 1) === s.position);
      for (const p of bucket) list.push({ ...p, _sectionName: s.name });
    }
    // Also include orphaned photos (unknown section) at the end
    for (const p of photos) {
      if (!byPos.has(p.section || 1)) list.push({ ...p, _sectionName: "Extras" });
    }
    return list;
  }, [photos, sections]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (selectedIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSelectedIdx(-1);
      if (e.key === "ArrowRight") setSelectedIdx((i) => Math.min(orderedPhotos.length - 1, i + 1));
      if (e.key === "ArrowLeft") setSelectedIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [selectedIdx, orderedPhotos.length]);

  const selected = selectedIdx >= 0 ? orderedPhotos[selectedIdx] : null;

  // Preload neighbors for silky lightbox transitions
  useEffect(() => {
    if (!selected) return;
    for (const off of [-1, 1]) {
      const p = orderedPhotos[selectedIdx + off];
      if (p) { const img = new Image(); img.src = imgUrl(p.image_path); }
    }
  }, [selectedIdx, orderedPhotos, selected]);

  const openPhoto = (photo) => {
    const idx = orderedPhotos.findIndex((p) => p.id === photo.id);
    if (idx >= 0) setSelectedIdx(idx);
  };

  return (
    <div className="relative bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="gallery-page">
      <Seo
        title="Gallery | SyndaFam Artwork, Music Videos & Visual Culture"
        description="SyndaFam gallery — cover art, cinematic music-video stills, behind-the-scenes photography and street-inspired visual art from the Toronto hip-hop collective."
        path="/gallery"
      />

      {/* Living aurora backdrop for the whole page */}
      <div className="fixed inset-0 -z-10 bg-aurora pointer-events-none opacity-70" aria-hidden="true" />
      <div className="fixed inset-0 -z-10 pointer-events-none" aria-hidden="true">
        <div className="orb orb-red orb-live"   style={{ width: 640, height: 640, top: "-160px", left: "-180px" }} />
        <div className="orb orb-amber orb-live" style={{ width: 520, height: 520, bottom: "-160px", right: "-140px", animationDelay: "-6s" }} />
        <div className="orb orb-cyan orb-live"  style={{ width: 360, height: 360, top: "50%", left: "45%", opacity: 0.18, animationDelay: "-3s" }} />
      </div>

      {/* HERO — cinematic full-viewport intro */}
      <section className="relative min-h-[92vh] flex flex-col justify-end px-5 sm:px-10 pb-16 sm:pb-24 pt-24" data-testid="gallery-hero">
        <div className="max-w-[1400px] mx-auto w-full">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-6 text-glow-red reveal reveal-1">
            // A VISUAL RECORD OF SYNDAFAM
          </div>
          <h1
            className="font-display leading-[0.82] tracking-tighter reveal reveal-2"
            style={{ fontSize: "clamp(3.5rem, 14vw, 14rem)" }}
          >
            EYES
            <br />
            <span className="text-[#FCA311] text-glow-amber">ONLY.</span>
          </h1>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-end reveal reveal-3">
            <p className="max-w-xl text-white/70 text-lg sm:text-xl leading-relaxed">
              Photographs from the booth, the block, the basement — an unfiltered visual record of the Toronto collective. Scroll to move through the chapters.
            </p>
            <div className="hidden lg:flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-white/50">
              <span>{photos.length} FRAMES</span>
              <span className="w-6 h-px bg-white/20" />
              <span>{sections.length} CHAPTERS</span>
            </div>
          </div>
          <div className="mt-14 flex items-center gap-3 text-white/50 font-mono text-[10px] tracking-[0.4em] reveal reveal-4" aria-hidden="true">
            <ArrowDown className="w-4 h-4 animate-bounce" /> SCROLL TO ENTER
          </div>
        </div>
      </section>

      {/* CHAPTERS */}
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 pb-32">
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-3xl bg-white/[0.03] animate-pulse" />
            ))}
          </div>
        )}

        {!loading && photos.length === 0 && (
          <div className="font-display text-3xl py-40 text-center text-white/40">
            NO FRAMES YET. PULL UP LATER.
          </div>
        )}

        {!loading && sections.map((s, sIdx) => {
          const sectionPhotos = photos.filter((p) => (p.section || 1) === s.position);
          if (sectionPhotos.length === 0) return null;
          const sizes = pickSizes(sectionPhotos.length);
          return (
            <section
              key={s.id}
              className="relative mb-24 sm:mb-32"
              data-testid={`gallery-section-${s.position}`}
            >
              <SectionIntro section={s} count={sectionPhotos.length} index={sIdx} />
              {/* Dynamic asymmetric grid */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 sm:gap-6" data-testid={`gallery-grid-${s.position}`}>
                {sectionPhotos.map((p, i) => {
                  const size = sizes[i];
                  // Span classes to create a magazine-like dynamic layout
                  const spans =
                    size === "wide"      ? "col-span-2 md:col-span-6" :
                    size === "landscape" ? "col-span-2 md:col-span-4" :
                    size === "portrait"  ? "col-span-1 md:col-span-2" :
                    size === "tall"      ? "col-span-1 md:col-span-2 md:row-span-2" :
                                           "col-span-1 md:col-span-2";
                  return (
                    <div key={p.id} className={spans}>
                      <PhotoCard photo={p} size={size} index={i} onClick={() => openPhoto(p)} />
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {/* CINEMATIC LIGHTBOX */}
      {selected && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          onClick={() => setSelectedIdx(-1)}
          data-testid="lightbox"
          style={{ animation: "lift-in .35s ease-out both" }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/85 backdrop-blur-xl" />
          {/* Ambient glow following image */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="orb orb-red"   style={{ width: 500, height: 500, top: "-80px", left: "10%" }} />
            <div className="orb orb-amber" style={{ width: 500, height: 500, bottom: "-100px", right: "10%" }} />
          </div>

          {/* Top bar */}
          <div className="absolute top-0 left-0 right-0 px-5 sm:px-10 py-5 flex items-center justify-between font-mono text-[10px] tracking-[0.4em] text-white/60 z-10" onClick={(e) => e.stopPropagation()}>
            <div>
              {String(selectedIdx + 1).padStart(2, "0")} / {String(orderedPhotos.length).padStart(2, "0")}
              {selected._sectionName && <span className="ml-4 text-[#FCA311]">// {selected._sectionName.toUpperCase()}</span>}
            </div>
            <button
              onClick={() => setSelectedIdx(-1)}
              className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-white hover:text-[#E63946] transition-colors"
              aria-label="Close"
              data-testid="lightbox-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Prev button */}
          {selectedIdx > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedIdx((i) => Math.max(0, i - 1)); }}
              className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full glass-card flex items-center justify-center text-white hover:text-[#FCA311] hover:border-[#FCA311] transition-colors"
              aria-label="Previous"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          {/* Next button */}
          {selectedIdx < orderedPhotos.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedIdx((i) => Math.min(orderedPhotos.length - 1, i + 1)); }}
              className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full glass-card flex items-center justify-center text-white hover:text-[#FCA311] hover:border-[#FCA311] transition-colors"
              aria-label="Next"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Image — key on id triggers fade */}
          <img
            key={selected.id}
            src={imgUrl(selected.image_path)}
            alt={selected.caption || "SyndaFam gallery photo"}
            className="relative max-h-[85vh] max-w-[92vw] object-contain rounded-2xl shadow-[0_40px_120px_rgba(0,0,0,0.9)]"
            style={{ animation: "lift-in .5s cubic-bezier(.2,.9,.25,1) both" }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Caption strip */}
          {selected.caption && (
            <div
              className="absolute bottom-0 left-0 right-0 px-5 sm:px-10 py-6 text-center z-10 pointer-events-none"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="inline-block max-w-3xl glass-card-strong rounded-2xl px-6 py-4 pointer-events-auto">
                <div className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311] mb-1">// FRAME</div>
                <div className="font-display text-lg sm:text-xl text-white leading-tight">{selected.caption}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
